import Close from "@mui/icons-material/Close";
import { Box, Button, Skeleton, Stack, Tooltip, Typography } from "@mui/material";
import type { ReactNode } from "react";

import {
	clearButtonSx,
	destructiveButtonSx,
	destructiveIconSx,
	gradientPrimaryButtonSx,
	selectionRowContainerSx,
} from "../global/components/datagrid/dataGridSelectionRowStyles";

export type SelectionAction = {
	label: string;
	icon?: ReactNode;
	onClick: () => void;
	disabled?: boolean;
	disabledTooltip?: string;
	variant: "primary" | "destructive";
};

export type SettingsDataGridSelectionRowProps = {
	resourceNoun: string;
	visibleCount: number;
	selectedCount: number;
	loading?: boolean;
	onClearSelection: () => void;
	actions?: SelectionAction[];
};

function pluralize(noun: string, count: number) {
	if (count === 1) {
		return noun;
	}

	if (noun.endsWith("s")) {
		return noun;
	}

	return `${noun}s`;
}

export default function SettingsDataGridSelectionRow(props: SettingsDataGridSelectionRowProps) {
	const {
		resourceNoun,
		visibleCount = 0,
		selectedCount = 0,
		loading = false,
		onClearSelection,
		actions = [],
	} = props;

	const hasSelection = selectedCount > 0;
	const allSelected = visibleCount > 0 && selectedCount === visibleCount;
	const idleNoun = pluralize(resourceNoun, visibleCount);
	const selectedNoun = pluralize(resourceNoun, selectedCount);

	const countLabel = loading
		? null
		: hasSelection
			? `${allSelected ? `All ${selectedCount}` : selectedCount} ${selectedNoun} selected`
			: visibleCount
				? `${visibleCount} ${idleNoun}`
				: `No ${pluralize(resourceNoun, 2)}`;

	return (
		<Box sx={selectionRowContainerSx}>
			<Stack direction="row" sx={{ alignItems: "center", gap: 1.5 }}>
				{loading ? (
					<Skeleton variant="text" width={80} height={20} />
				) : (
					<Typography sx={{ fontSize: 13, fontWeight: 600, color: hasSelection ? "rgba(13, 31, 48, 0.86)" : "rgba(13, 31, 48, 0.45)" }}>
						{countLabel}
					</Typography>
				)}
				{hasSelection ? (
					<>
						<Box sx={{ height: 18, width: "1px", background: "rgba(13, 31, 48, 0.08)" }} />
						<Button
							size="small"
							startIcon={<Close sx={{ fontSize: 16 }} />}
							onClick={onClearSelection}
							sx={clearButtonSx}
						>
							Clear
						</Button>
					</>
				) : null}
			</Stack>

			{actions.length > 0 ? (
				<Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
					{actions.map((action) => {
						const isDisabled = !hasSelection || action.disabled;
						const button = (
							<Button
								key={action.label}
								size="small"
								startIcon={action.icon}
								onClick={action.onClick}
								disabled={isDisabled}
								sx={action.variant === "destructive" ? destructiveButtonSx : gradientPrimaryButtonSx}
							>
								{action.label}
							</Button>
						);

						if (isDisabled && action.disabledTooltip) {
							return (
								<Tooltip key={action.label} title={action.disabledTooltip} arrow>
									<span>{button}</span>
								</Tooltip>
							);
						}

						return button;
					})}
				</Stack>
			) : null}
		</Box>
	);
}

export { destructiveIconSx };
